/**
 * The page's event tail, kept in `sessionStorage` per session so a reload of
 * the page reads a turn's tail back. The page holds the tail in the desktop
 * app and in development alike, since it runs the event stream itself.
 * Writes are debounced per session and flushed when the page hides; a tail
 * the cap cut is not restored, so the page rebuilds from the saved messages.
 * A reload loses the events between the page hiding and the new stream
 * connecting, so a part still streaming shows that gap until it ends. A turn
 * that ends in that gap keeps its restored tail until the session's next
 * turn, a reconnect or its deletion, since the stream never sends that end;
 * a read leaves the tail out once the messages hold the turn's success
 * (`usableTail` in `local-runtime.ts`).
 */
import type { OpenCodeEvent } from '@/lib/code-sessions/local-agent/opencode-api';

import { EventTail } from '@/lib/code-sessions/local-agent/event-tail';
import { sessionIdOf } from '@/lib/code-sessions/local-agent/opencode-api';

export const TAIL_STORAGE_PREFIX = 'cubic:local-agent:tail:';

const WRITE_DELAY_MS = 300;

type StoredTail = { events: readonly OpenCodeEvent[] };

function storedKeys(storage: Storage): string[] {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key !== null && key.startsWith(TAIL_STORAGE_PREFIX)) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * The events a stored entry holds, when a tail of `capacity` reads them back
 * whole; null for an entry this page cannot read or one the cap would cut.
 * Read into a tail of its own first, so a bad entry costs no other session
 * its restored tail.
 */
function restorable(
  value: string | null,
  sessionId: string,
  capacity: number | undefined,
): readonly OpenCodeEvent[] | null {
  try {
    const stored = JSON.parse(value ?? '') as StoredTail;
    const probe = new EventTail(capacity);
    for (const event of stored.events) {
      probe.push(event);
    }
    const events = probe.eventsFor(sessionId);
    return events.length === 0 || probe.truncatedFor(sessionId) ? null : events;
  } catch {
    return null;
  }
}

/**
 * An event tail that survives a reload of the page through `storage`; without
 * one, a plain tail.
 */
export function storedTail(storage: Storage | null, capacity?: number): EventTail {
  const tail = new EventTail(capacity);
  if (storage === null) {
    return tail;
  }
  for (const key of storedKeys(storage)) {
    const sessionId = key.slice(TAIL_STORAGE_PREFIX.length);
    const events = restorable(storage.getItem(key), sessionId, capacity);
    if (events === null) {
      storage.removeItem(key);
      continue;
    }
    for (const event of events) {
      tail.push(event);
    }
  }
  const pending = new Map<string, ReturnType<typeof setTimeout>>();
  const write = (sessionId: string) => {
    pending.delete(sessionId);
    const key = `${TAIL_STORAGE_PREFIX}${sessionId}`;
    const events = tail.eventsFor(sessionId);
    if (events.length === 0 || tail.truncatedFor(sessionId)) {
      storage.removeItem(key);
      return;
    }
    try {
      storage.setItem(key, JSON.stringify({ events } satisfies StoredTail));
    } catch {
      // Out of room: the next reload rebuilds from the saved messages.
      storage.removeItem(key);
    }
  };
  const flush = () => {
    for (const sessionId of [...pending.keys()]) {
      clearTimeout(pending.get(sessionId));
      write(sessionId);
    }
  };
  tail.subscribe((signal) => {
    if (signal.kind === 'resync') {
      // The tail dropped every session's events, so the storage drops every entry, pending
      // writes included.
      for (const timer of pending.values()) {
        clearTimeout(timer);
      }
      pending.clear();
      for (const key of storedKeys(storage)) {
        storage.removeItem(key);
      }
      return;
    }
    const sessionId = sessionIdOf(signal.event);
    if (sessionId === null) {
      return;
    }
    if (!pending.has(sessionId)) {
      pending.set(
        sessionId,
        setTimeout(() => write(sessionId), WRITE_DELAY_MS),
      );
    }
  });
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flush);
  }
  return tail;
}
